/** Homepage glide, a greeting beside a name, or a visit that lands on a public page. */

const DEG = Math.PI / 180;

export function initPlane() {
  const plane = document.querySelector('[data-paper-plane]');
  if (!plane || plane.dataset.state) return;
  if (plane.dataset.flight === 'greeting') {
    initGreeting(plane);
    return;
  }
  if (plane.dataset.flight === 'visit' || plane.dataset.flight === 'note') {
    initVisit(plane);
    return;
  }
  const section = plane?.closest('section');
  const stage = section?.querySelector('[data-plane-stage]');
  const craft = plane?.querySelector('[data-plane-craft]');
  const shadow = plane?.querySelector('[data-plane-shadow]');
  if (!section || !stage || !craft || !shadow) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let size = readSize();
  let parked = false;
  let playing = false;
  let resting = { x: 0, y: 0, rest: 0 };
  let layoutWidth = section.clientWidth;
  let layoutHeight = section.clientHeight;

  const show = () => {
    plane.hidden = false;
    plane.classList.add('is-live');
  };

  const parkNow = () => {
    if (parked) return;
    parked = true;
    plane.dataset.state = 'landed';
    size = readSize();
    const place = measureLanding();
    apply(place, { heading: place.rest });
    craft.style.willChange = '';
    shadow.style.willChange = '';
    requestAnimationFrame(() => plane.classList.add('is-resting'));
  };

  const begin = () => {
    show();
    if (reduce.matches) {
      parkNow();
      return;
    }
    plane.dataset.state = 'flying';
    craft.style.willChange = 'transform';
    shadow.style.willChange = 'transform, opacity, filter';
    const stop = fly();
    reduce.addEventListener('change', () => {
      if (!reduce.matches) return;
      stop();
      parkNow();
    });
  };

  const launch = () => {
    if (document.hidden) {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) launch();
      }, { once: true });
      return;
    }
    begin();
  };
  const fonts = document.fonts?.ready ?? Promise.resolve();
  const fontCap = new Promise((resolve) => setTimeout(resolve, 450));
  Promise.race([fonts, fontCap]).then(launch);

  const observer = new ResizeObserver(() => {
    const width = section.clientWidth;
    const height = section.clientHeight;
    if (width === layoutWidth && height === layoutHeight) return;
    layoutWidth = width;
    layoutHeight = height;
    if (!parked || playing) return;
    size = readSize();
    const place = measureLanding();
    const far = Math.hypot(place.x - resting.x, place.y - resting.y) > 180;
    if (far) plane.classList.remove('is-resting');
    apply(place, { heading: place.rest });
    if (far) {
      void craft.offsetWidth;
      plane.classList.add('is-resting');
    }
  });
  observer.observe(section);
  armPlaneClick({
    plane,
    craft,
    reduce,
    getSize: () => size = readSize(),
    getPlace: () => measureLanding(),
    apply,
    setPlaying: (value) => { playing = value; },
  });

  function readSize() {
    const style = getComputedStyle(plane);
    return {
      w: parseFloat(style.getPropertyValue('--plane-w')) || 64,
      h: parseFloat(style.getPropertyValue('--plane-h')) || 32,
    };
  }

  function measureLanding() {
    const view = measureScene();
    const cruise = clamp(view.sky * 0.44, size.h * 0.56, Math.max(size.h * 0.56, view.sky - size.h * 0.42));
    // Arrives from the right, so the nose points left, just clear of the button.
    const rest = Math.PI - 6 * DEG;
    const noseLead = size.w * 0.4;
    let x = view.perch.right + 28 + noseLead;
    const y = view.perch.midY;
    if (view.sideBySide) x = Math.min(x, view.card.left - size.w * 0.52 - 8);
    x = clamp(x, view.perch.right + 14 + noseLead * 0.5, view.width - size.w * 0.5 - 10);

    const textRight = Math.max(view.h1?.right ?? 0, view.copy?.right ?? 0);
    const gutterX = view.sideBySide
      ? textRight + (view.card.left - textRight) * 0.4
      : view.contentRight - size.w * 0.72;
    const gap = view.perch.top - (view.copy?.bottom ?? view.h1?.bottom ?? view.sky);
    const bandY = gap > size.h + 14 ? (view.copy?.bottom ?? view.sky) + gap * 0.5 : y;
    const lane = view.sideBySide
      ? view.card.top - size.h - 10
      : Math.max(size.h, (view.h1?.top ?? view.sky) - size.h * 0.75);

    return {
      x,
      y,
      rest,
      cruise,
      width: view.width,
      stacked: !view.sideBySide,
      gutterX,
      bandY,
      lane,
      contentLeft: view.contentLeft,
      contentRight: view.contentRight,
      h1Top: view.h1?.top ?? view.sky,
      h1Bottom: view.h1?.bottom ?? (view.h1?.top ?? view.sky),
      height: view.height,
      obstacles: view.obstacles,
    };
  }

  function measureScene() {
    const sectionRect = section.getBoundingClientRect();
    const sky = parseFloat(getComputedStyle(stage).paddingTop) || 64;
    const box = (el) => {
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      return {
        left: rect.left - sectionRect.left,
        right: rect.right - sectionRect.left,
        top: rect.top - sectionRect.top,
        bottom: rect.bottom - sectionRect.top,
        midY: (rect.top + rect.bottom) / 2 - sectionRect.top,
      };
    };
    const perch = box(section.querySelector('[data-plane-perch]'));
    const card = box(section.querySelector('[data-plane-land]'));
    const h1 = box(section.querySelector('h1'));
    const copy = box(h1 && section.querySelector('h1')?.nextElementSibling);
    const stageRect = stage.getBoundingClientRect();
    const stageStyle = getComputedStyle(stage);
    const padL = parseFloat(stageStyle.paddingLeft) || 0;
    const padR = parseFloat(stageStyle.paddingRight) || 0;
    const width = section.clientWidth;
    const sideBySide = Boolean(card && perch && card.left > width * 0.42 && card.top < perch.top);
    const obstacles = [...stage.querySelectorAll('h1, p, a, dl')].map((el) => {
      const rect = el.getBoundingClientRect();
      return {
        left: rect.left - sectionRect.left,
        right: rect.right - sectionRect.left,
        top: rect.top - sectionRect.top,
        bottom: rect.bottom - sectionRect.top,
      };
    }).filter((item) => item.right > item.left + 1 && item.bottom > item.top + 1);
    return {
      sky,
      perch,
      card,
      h1,
      copy,
      width,
      height: section.clientHeight,
      sideBySide,
      obstacles,
      contentLeft: stageRect.left - sectionRect.left + padL,
      contentRight: stageRect.right - sectionRect.left - padR,
    };
  }

  function apply(place, { heading = place.rest, bank = 1, depth = 1, altitude = 0, opacity = 1, wobble = 0, shadowX = place.x, shadowY = place.y } = {}) {
    resting = place;
    const aim = heading ?? place.rest;
    const nx = -Math.sin(aim);
    const ny = Math.cos(aim);
    const cx = place.x + nx * wobble;
    const cy = place.y + ny * wobble;
    craft.style.opacity = String(opacity);
    craft.style.transform = `translate3d(${cx - size.w / 2}px, ${cy - size.h / 2}px, 0) rotate(${aim * 180 / Math.PI}deg) scale(${depth}, ${depth * bank})`;

    const drop = size.h * 0.4 + altitude * 0.32;
    const shadowScale = 1 + altitude / 260;
    const blur = 1.7 + altitude / 16;
    shadow.style.opacity = String(0.18 * (1 - Math.min(0.7, altitude / 190)) * opacity);
    shadow.style.filter = `blur(${blur}px)`;
    shadow.style.transform = `translate3d(${shadowX - drop * 0.1}px, ${shadowY + drop}px, 0) translate(-50%, -50%) scale(${shadowScale}, 1)`;
  }

  function fly() {
    const land = measureLanding();
    const path = buildPath(land, size);
    const duration = clamp(path.length / 0.21, 5200, 9200);
    let raf = 0;
    let stopped = false;
    let origin = 0;
    let last = 0;
    let heading = tangentAt(path, 0);
    let bank = 1;
    let arrived = 0;

    const step = (now) => {
      if (stopped || parked) return;
      if (!origin) {
        origin = now + 240;
        last = now;
      }
      if (now < origin) {
        const start = pointAt(path, 0);
        apply(placeAt(start, land.rest), { heading, altitude: Math.max(0, land.y - start.y) });
        raf = requestAnimationFrame(step);
        return;
      }

      const elapsed = now - origin;
      const t = clamp(elapsed / duration, 0, 1);
      const pos = pointAtClock(path, ease(t) * path.clock);
      const dt = Math.min(0.034, (now - last) / 1000 || 0.016);
      last = now;
      const target = t < 0.995 ? tangentAt(path, pos.d) : land.rest;
      const preview = tangentAt(path, Math.min(path.length, pos.d + 26));
      const bend = Math.abs(shortest(target, preview));
      const looping = bend > 0.2;
      const previous = heading;
      heading += shortest(heading, target) * (1 - Math.exp(-dt / (looping ? 0.07 : 0.28)));
      const turnRate = Math.abs(shortest(previous, heading) / dt);
      const bankTarget = 1 - Math.min(looping ? 0.16 : 0.07, turnRate * 0.03);
      bank += (bankTarget - bank) * (1 - Math.exp(-dt / 0.16));

      const envelope = Math.sin(Math.min(1, t / 0.14) * Math.PI / 2) * Math.pow(1 - t, 1.35);
      const flutter = Math.sin(elapsed * 0.009) * (looping ? 0.55 : 2.1) * DEG * envelope;
      const wobble = Math.sin(elapsed * 0.0072) * (looping ? 1.1 : 3.2) * envelope;
      const altitude = Math.max(0, land.y - pos.y);
      const depth = clamp(1 - altitude / 1200, 0.95, 1);
      const spot = t > 0.992 ? { ...land } : placeAt(pos, land.rest);
      apply(spot, { heading: heading + flutter, bank, depth, altitude, wobble });

      if (t >= 1) {
        if (!arrived) arrived = now;
        const waited = now - arrived;
        const noseReady = Math.abs(shortest(heading, land.rest)) < 0.02;
        if ((noseReady && waited > 260) || waited > 640) {
          stopped = true;
          parkNow();
          return;
        }
      }
      raf = requestAnimationFrame(step);
    };

    const start = pointAt(path, 0);
    apply(placeAt(start, heading), { heading, altitude: Math.max(0, land.y - start.y) });
    raf = requestAnimationFrame(step);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
  }
}

function initGreeting(plane) {
  const stage = plane.closest('[data-plane-stage]');
  const craft = plane.querySelector('[data-plane-craft]');
  const shadow = plane.querySelector('[data-plane-shadow]');
  const name = stage?.querySelector('[data-plane-name]');
  if (!stage || !craft || !shadow || !name) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let size = { w: 64, h: 32 };
  let parked = false;
  let playing = false;
  let resting = { x: 0, y: 0, rest: 0 };
  let layoutWidth = stage.clientWidth;
  let layoutHeight = stage.clientHeight;

  const show = () => {
    plane.hidden = false;
    plane.classList.add('is-live');
  };

  const readSize = () => {
    const style = getComputedStyle(plane);
    return {
      w: parseFloat(style.getPropertyValue('--plane-w')) || 64,
      h: parseFloat(style.getPropertyValue('--plane-h')) || 32,
    };
  };

  const parkNow = () => {
    if (parked) return;
    parked = true;
    plane.dataset.state = 'landed';
    size = readSize();
    const place = measureGreeting();
    apply(place, { heading: place.rest });
    craft.style.willChange = '';
    shadow.style.willChange = '';
    requestAnimationFrame(() => plane.classList.add('is-resting'));
  };

  const begin = () => {
    show();
    size = readSize();
    if (reduce.matches) {
      parkNow();
      return;
    }
    plane.dataset.state = 'flying';
    craft.style.willChange = 'transform';
    shadow.style.willChange = 'transform, opacity, filter';
    const stop = fly();
    reduce.addEventListener('change', () => {
      if (!reduce.matches) return;
      stop();
      parkNow();
    });
  };

  if (document.hidden) {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) begin();
    }, { once: true });
  } else {
    begin();
  }

  const observer = new ResizeObserver(() => {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    if (width === layoutWidth && height === layoutHeight) return;
    layoutWidth = width;
    layoutHeight = height;
    if (!parked || playing) return;
    size = readSize();
    const place = measureGreeting();
    const far = Math.hypot(place.x - resting.x, place.y - resting.y) > 140;
    if (far) plane.classList.remove('is-resting');
    apply(place, { heading: place.rest });
    if (far) {
      void craft.offsetWidth;
      plane.classList.add('is-resting');
    }
  });
  observer.observe(stage);
  armPlaneClick({
    plane,
    craft,
    reduce,
    getSize: () => size = readSize(),
    getPlace: () => measureGreeting(),
    apply,
    setPlaying: (value) => { playing = value; },
  });

  function measureGreeting() {
    const stageRect = stage.getBoundingClientRect();
    const nameBox = inkBox(name);
    const rest = 4 * DEG;
    const tail = size.w * 0.48;
    const nose = size.w * 0.44;
    const nameRight = nameBox.right - stageRect.left;
    const nameTop = nameBox.top - stageRect.top;
    const nameMid = nameBox.midY - stageRect.top;
    let x = nameRight + 16 + tail;
    let y = nameMid;

    const copy = stage.querySelector('[data-plane-copy]');
    if (copy) {
      const copyBox = inkBox(copy);
      const copyTop = copyBox.top - stageRect.top;
      const copyRight = copyBox.right - stageRect.left;
      if (copyRight > nameRight - 8 && y + size.h * 0.4 > copyTop) {
        y = Math.min(y, copyTop - size.h * 0.42 - 4);
      }
    }

    const avoid = stage.querySelector('[data-plane-avoid]');
    const avoidRect = avoid?.getBoundingClientRect();
    if (avoidRect && avoidRect.width > 0) {
      const avoidTop = avoidRect.top - stageRect.top;
      const avoidBottom = avoidRect.bottom - stageRect.top;
      if (avoidTop < y + size.h * 0.45 && avoidBottom > y - size.h * 0.45) {
        x = Math.min(x, avoidRect.left - stageRect.left - nose - 12);
      }
    }
    x = Math.min(x, stage.clientWidth - nose - 8);
    x = Math.max(x, nameRight + 10 + tail * 0.85);

    const main = stage.closest('main');
    const mainStyle = main ? getComputedStyle(main) : null;
    const padLeft = parseFloat(mainStyle?.paddingLeft) || 16;
    const sky = Math.min(-6, -(size.h * 0.34 + 5));
    const startX = -padLeft + size.w * 0.48 + 4;
    const kicker = stage.querySelector('[data-plane-kicker]');
    const kickerRight = kicker ? inkBox(kicker).right - stageRect.left : startX + size.w;

    return { x, y, rest, nameRight, nameTop, sky, startX, kickerRight };
  }

  function apply(place, { heading = place.rest, bank = 1, depth = 1, altitude = 0, opacity = 1, wobble = 0, shadowX = place.x, shadowY = place.y } = {}) {
    resting = place;
    const aim = heading ?? place.rest;
    const nx = -Math.sin(aim);
    const ny = Math.cos(aim);
    const cx = place.x + nx * wobble;
    const cy = place.y + ny * wobble;
    craft.style.opacity = String(opacity);
    craft.style.transform = `translate3d(${cx - size.w / 2}px, ${cy - size.h / 2}px, 0) rotate(${aim * 180 / Math.PI}deg) scale(${depth}, ${depth * bank})`;

    const drop = size.h * 0.42 + altitude * 0.22;
    const shadowScale = 1 + altitude / 280;
    const blur = 1.5 + altitude / 22;
    const lift = 1 - Math.min(0.92, altitude / 64);
    shadow.style.opacity = String(0.14 * lift * opacity);
    shadow.style.filter = `blur(${blur}px)`;
    shadow.style.transform = `translate3d(${shadowX - drop * 0.08}px, ${shadowY + drop}px, 0) translate(-50%, -50%) scale(${shadowScale}, 1)`;
  }

  function fly() {
    const land = measureGreeting();
    const path = greetingPath(land, size);
    const duration = clamp(path.length / 0.22, 2400, 3200);
    let raf = 0;
    let stopped = false;
    let origin = 0;
    let last = 0;
    let heading = tangentAt(path, 0);
    let bank = 1;
    let arrived = 0;

    const step = (now) => {
      if (stopped || parked) return;
      if (!origin) {
        origin = now + 180;
        last = now;
      }
      if (now < origin) {
        const start = pointAt(path, 0);
        apply(placeAt(start, land.rest), { heading, altitude: Math.max(0, land.y - start.y) });
        raf = requestAnimationFrame(step);
        return;
      }

      const elapsed = now - origin;
      const t = clamp(elapsed / duration, 0, 1);
      const distance = greetEase(t) * path.length;
      const pos = pointAt(path, distance);
      const dt = Math.min(0.034, (now - last) / 1000 || 0.016);
      last = now;
      const raw = t < 0.992 ? tangentAt(path, distance) : land.rest;
      const target = clamp(raw, land.rest - 8 * DEG, 11 * DEG);
      const previous = heading;
      heading += shortest(heading, target) * (1 - Math.exp(-dt / 0.18));
      const turnRate = Math.abs(shortest(previous, heading) / dt);
      const bankTarget = 1 - Math.min(0.05, turnRate * 0.025);
      bank += (bankTarget - bank) * (1 - Math.exp(-dt / 0.16));

      const envelope = Math.sin(Math.min(1, t / 0.16) * Math.PI / 2) * (1 - t) ** 1.6;
      const flutter = Math.sin(elapsed * 0.008) * 1.15 * DEG * envelope;
      const wobble = Math.sin(elapsed * 0.0064) * 1.4 * envelope;
      const altitude = Math.max(0, land.y - pos.y);
      const spot = t > 0.992 ? { ...land } : placeAt(pos, land.rest);
      apply(spot, { heading: heading + flutter, bank, altitude, wobble });

      if (t >= 1) {
        if (!arrived) arrived = now;
        const waited = now - arrived;
        const noseReady = Math.abs(shortest(heading, land.rest)) < 0.02;
        if ((noseReady && waited > 180) || waited > 480) {
          stopped = true;
          parkNow();
          return;
        }
      }
      raf = requestAnimationFrame(step);
    };

    const start = pointAt(path, 0);
    apply(placeAt(start, heading), { heading, altitude: Math.max(0, land.y - start.y) });
    raf = requestAnimationFrame(step);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
  }
}

function initVisit(plane) {
  const scene = plane.closest('[data-plane-scene]');
  const craft = plane.querySelector('[data-plane-craft]');
  const shadow = plane.querySelector('[data-plane-shadow]');
  if (!scene || !craft || !shadow) return;

  const note = plane.dataset.flight === 'note';
  const onInk = plane.classList.contains('paper-plane--on-ink');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wideQuery = window.matchMedia('(min-width: 1024px)');
  let size = readSize();
  let parked = false;
  let playing = false;
  let resting = { x: 0, y: 0, rest: 0 };
  let layoutWidth = scene.clientWidth;
  let layoutHeight = scene.clientHeight;
  let rockRaf = 0;
  let onScreen = true;

  const show = () => {
    plane.hidden = false;
    plane.classList.add('is-live');
  };

  const parkNow = () => {
    if (parked) return;
    parked = true;
    plane.dataset.state = 'landed';
    const place = measure();
    apply(place, { heading: place.rest });
    craft.style.willChange = '';
    shadow.style.willChange = '';
    requestAnimationFrame(() => plane.classList.add('is-resting'));
    startRock();
  };

  const begin = () => {
    const field = scene.querySelector('[data-plane-focus]');
    if (field) {
      const top = field.getBoundingClientRect().top;
      if (top > 48 && top < window.innerHeight - 72) field.focus({ preventScroll: true });
    }
    show();
    if (reduce.matches) {
      parkNow();
      return;
    }
    plane.dataset.state = 'flying';
    craft.style.willChange = 'transform';
    shadow.style.willChange = 'transform, opacity, filter';
    const stop = fly();
    reduce.addEventListener('change', () => {
      if (!reduce.matches) return;
      stop();
      parkNow();
    });
  };

  const launch = () => {
    if (document.hidden) {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) launch();
      }, { once: true });
      return;
    }
    begin();
  };

  const fonts = document.fonts?.ready ?? Promise.resolve();
  const fontCap = new Promise((resolve) => setTimeout(resolve, 450));
  Promise.race([fonts, fontCap]).then(launch);

  const seen = new IntersectionObserver(([entry]) => {
    onScreen = entry.isIntersecting;
  });
  seen.observe(scene);

  const observer = new ResizeObserver(() => {
    const width = scene.clientWidth;
    const height = scene.clientHeight;
    if (width === layoutWidth && height === layoutHeight) return;
    layoutWidth = width;
    layoutHeight = height;
    if (!parked || playing) return;
    const place = measure();
    const far = Math.hypot(place.x - resting.x, place.y - resting.y) > 160;
    resting = { x: place.x, y: place.y, rest: place.rest };
    if (note && !reduce.matches) return;
    if (far) plane.classList.remove('is-resting');
    apply(place, { heading: place.rest });
    if (far) {
      void craft.offsetWidth;
      plane.classList.add('is-resting');
    }
  });
  observer.observe(scene);
  armPlaneClick({
    plane,
    craft,
    reduce,
    getSize: () => size = readSize(),
    getPlace: () => measure(),
    apply,
    setPlaying: (value) => { playing = value; },
    onLift: stopRock,
    onRest: startRock,
  });

  function readSize() {
    const style = getComputedStyle(plane);
    return {
      w: parseFloat(style.getPropertyValue('--plane-w')) || 64,
      h: parseFloat(style.getPropertyValue('--plane-h')) || 32,
    };
  }

  function measure() {
    size = readSize();
    return planVisit(scene, plane, size, wideQuery.matches);
  }

  function stopRock() {
    cancelAnimationFrame(rockRaf);
  }

  function startRock() {
    stopRock();
    if (!note || reduce.matches || !parked) return;
    const tick = (now) => {
      if (!parked || playing) return;
      if (reduce.matches) {
        apply(resting, { heading: resting.rest, remember: false });
        return;
      }
      if (!document.hidden && onScreen) {
        const place = resting;
        const wave = Math.sin(now / 460) * 3.1 * DEG + Math.sin(now / 290) * 0.8 * DEG;
        const bob = Math.sin(now / 620) * 1.8 + Math.sin(now / 240) * 0.45;
        apply(
          { x: place.x, y: place.y + bob, rest: place.rest },
          {
            heading: place.rest + wave,
            altitude: Math.max(0, -bob) * 0.45,
            shadowX: place.x,
            shadowY: place.y,
            remember: false,
          },
        );
      }
      rockRaf = requestAnimationFrame(tick);
    };
    rockRaf = requestAnimationFrame(tick);
  }

  function apply(place, { heading = place.rest, bank = 1, depth = 1, altitude = 0, opacity = 1, wobble = 0, shadowX = place.x, shadowY = place.y, remember = true } = {}) {
    if (remember) resting = { x: place.x, y: place.y, rest: place.rest };
    const aim = heading ?? place.rest;
    const nx = -Math.sin(aim);
    const ny = Math.cos(aim);
    const cx = place.x + nx * wobble;
    const cy = place.y + ny * wobble;
    craft.style.opacity = String(opacity);
    craft.style.transform = `translate3d(${cx - size.w / 2}px, ${cy - size.h / 2}px, 0) rotate(${aim * 180 / Math.PI}deg) scale(${depth}, ${depth * bank})`;

    const drop = size.h * 0.4 + altitude * 0.32;
    const shadowScale = 1 + altitude / 260;
    const blur = 1.7 + altitude / 16;
    const shadowBase = onInk ? 0.2 : 0.18;
    shadow.style.opacity = String(shadowBase * (1 - Math.min(0.7, altitude / 190)) * opacity);
    shadow.style.filter = `blur(${blur}px)`;
    shadow.style.transform = `translate3d(${shadowX - drop * 0.1}px, ${shadowY + drop}px, 0) translate(-50%, -50%) scale(${shadowScale}, 1)`;
  }

  function fly() {
    const land = measure();
    const path = buildVisitPath(land, size);
    const duration = clamp(
      path.length / (land.trick === 'loop' ? 0.2 : 0.27),
      land.trick === 'loop' ? 4200 : 2800,
      land.trick === 'loop' ? 6400 : 5400,
    );
    const lag = land.trick === 'loop' ? 0.15 : 0.26;
    const flutterAmp = land.trick === 'loop' ? 0.85 : 2.05;
    let raf = 0;
    let stopped = false;
    let origin = 0;
    let last = 0;
    let heading = tangentAt(path, 0);
    let bank = 1;
    let arrived = 0;

    const step = (now) => {
      if (stopped || parked) return;
      if (!origin) {
        origin = now + (land.trick === 'loop' ? 160 : 220);
        last = now;
      }
      if (now < origin) {
        const start = pointAt(path, 0);
        apply(placeAt(start, land.rest), { heading, altitude: Math.max(0, land.y - start.y) });
        raf = requestAnimationFrame(step);
        return;
      }

      const elapsed = now - origin;
      const t = clamp(elapsed / duration, 0, 1);
      const pos = pointAtClock(path, ease(t) * path.clock);
      const dt = Math.min(0.034, (now - last) / 1000 || 0.016);
      last = now;
      const target = t < 0.995 ? tangentAt(path, pos.d) : land.rest;
      const previous = heading;
      heading += shortest(heading, target) * (1 - Math.exp(-dt / lag));
      const turnRate = Math.abs(shortest(previous, heading) / dt);
      const bankTarget = 1 - Math.min(land.trick === 'loop' ? 0.1 : 0.07, turnRate * 0.028);
      bank += (bankTarget - bank) * (1 - Math.exp(-dt / 0.16));

      const envelope = Math.sin(Math.min(1, t / 0.14) * Math.PI / 2) * (1 - t) ** 1.35;
      const flutter = Math.sin(elapsed * 0.009) * flutterAmp * DEG * envelope;
      const slip = land.trick === 'loop' && t > 0.78 && t < 0.96
        ? Math.sin((t - 0.78) / 0.18 * Math.PI) * 5
        : 0;
      const wobble = Math.sin(elapsed * 0.0072) * 3.2 * envelope + slip;
      const altitude = Math.max(0, land.y - pos.y);
      const depth = clamp(1 - altitude / 1200, 0.95, 1);
      const spot = t > 0.992 ? { ...land } : placeAt(pos, land.rest);
      apply(spot, { heading: heading + flutter, bank, depth, altitude, wobble });

      if (t >= 1) {
        if (!arrived) arrived = now;
        const waited = now - arrived;
        const noseReady = Math.abs(shortest(heading, land.rest)) < 0.02;
        if ((noseReady && waited > 240) || waited > 640) {
          stopped = true;
          parkNow();
          return;
        }
      }
      raf = requestAnimationFrame(step);
    };

    const start = pointAt(path, 0);
    apply(placeAt(start, heading), { heading, altitude: Math.max(0, land.y - start.y) });
    raf = requestAnimationFrame(step);
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
    };
  }
}

function planVisit(scene, plane, size, wide) {
  const sceneRect = scene.getBoundingClientRect();
  const box = (el) => {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) return null;
    return {
      left: rect.left - sceneRect.left,
      right: rect.right - sceneRect.left,
      top: rect.top - sceneRect.top,
      bottom: rect.bottom - sceneRect.top,
      midY: (rect.top + rect.bottom) / 2 - sceneRect.top,
    };
  };
  const stage = scene.querySelector('[data-plane-stage]');
  const stageRect = (stage || scene).getBoundingClientRect();
  const stageStyle = getComputedStyle(stage || scene);
  const ctx = {
    width: scene.clientWidth,
    height: scene.clientHeight,
    size,
    wide,
    fold: window.innerHeight - sceneRect.top,
    kicker: box(scene.querySelector('[data-plane-kicker]')),
    perch: box(scene.querySelector('[data-plane-perch]')),
    photo: box(scene.querySelector('[data-plane-photo]')),
    actions: cluster(scene.querySelector('[data-plane-actions]'), sceneRect),
    h1Ink: inkOf(scene.querySelector('h1'), sceneRect),
    kickerInk: inkOf(scene.querySelector('[data-plane-kicker]'), sceneRect),
    form: box(scene.querySelector('[data-plane-form]')),
    aside: box(scene.querySelector('[data-plane-aside]')),
    logo: box(scene.querySelector('[data-plane-logo]')),
    h1: box(scene.querySelector('h1')),
    edges: {
      left: stageRect.left - sceneRect.left + (parseFloat(stageStyle.paddingLeft) || 0),
      right: stageRect.right - sceneRect.left - (parseFloat(stageStyle.paddingRight) || 0),
    },
    obstacles: obstaclesIn(scene, sceneRect),
  };
  const pose = (!wide && plane.dataset.poseMobile) ? plane.dataset.poseMobile : (plane.dataset.pose || 'kicker');
  const from = (!wide && plane.dataset.fromMobile) ? plane.dataset.fromMobile : (plane.dataset.from || 'left');
  const spot = fitSpot(rawSpot(pose, from, ctx), ctx, pose);
  return routeFor({ ...spot, from }, pose, ctx);
}

function rawSpot(pose, from, ctx) {
  if ((pose === 'lip-right' || pose === 'lip-left') && ctx.photo) return lipSpot(ctx, pose === 'lip-right' ? 'right' : 'left');
  if (pose === 'present' && ctx.actions && ctx.photo) return presentSpot(ctx) || lipSpot(ctx, 'right');
  if (pose === 'sky' && (ctx.perch || ctx.kicker)) return skySpot(ctx) || besideKicker(ctx, from);
  if (pose === 'deliver' && ctx.form) return deliverSpot(ctx, from);
  if (pose === 'cheers') return cheersSpot(ctx, from);
  if (pose === 'jaunt') {
    const spot = besideKicker(ctx, from);
    const kickerTop = ctx.kicker?.top ?? spot.y;
    const lifted = kickerTop - ctx.size.h * 0.72;
    if (lifted > ctx.size.h * 0.62) return { ...spot, y: lifted, rest: cock(from) };
    return { ...spot, rest: cock(from) };
  }
  return besideKicker(ctx, from);
}

function lipSpot(ctx, side) {
  const { size, photo } = ctx;
  if (side === 'right') {
    return { x: photo.right - size.w * 0.62, y: photo.top, rest: Math.PI - 5 * DEG };
  }
  return { x: photo.left + size.w * 0.62, y: photo.top, rest: 6 * DEG };
}

function presentSpot(ctx) {
  const { size, actions, photo } = ctx;
  const x = actions.right + size.w * 0.64 + 16;
  const maxX = photo.left - size.w * 0.42;
  if (x > maxX + size.w * 0.2) return lipSpot(ctx, 'left');
  return {
    x: Math.min(x, maxX),
    y: actions.midY,
    rest: Math.PI - 6 * DEG,
  };
}

function skySpot(ctx) {
  const { size, kicker, perch, logo, aside } = ctx;
  const anchor = kicker || perch;
  const ceiling = (logo?.bottom ?? 0) + size.h * 0.7;
  const y = anchor.top - size.h * 0.9;
  if (y < ceiling) return null;
  const asideRight = aside?.right ?? ctx.width;
  const textRight = perch?.right ?? anchor.right;
  const x = clamp(textRight - size.w * 0.22, (perch?.left ?? anchor.left) + size.w * 0.3, asideRight - size.w * 0.64);
  return { x, y, rest: 5 * DEG };
}

function deliverSpot(ctx, from) {
  const { size, form } = ctx;
  return {
    x: clamp(form.left + size.w * 0.82, form.left + size.w * 0.5, form.right - size.w * 0.58),
    y: form.top - size.h * 0.12,
    rest: cock(from),
  };
}

function cheersSpot(ctx, from) {
  const { size, edges } = ctx;
  const ink = ctx.h1Ink || ctx.h1 || ctx.kickerInk || ctx.kicker;
  const rest = cock(from);
  const leftLimit = edges.left + size.w * 0.48;
  const rightLimit = edges.right - size.w * 0.48;
  const wanted = ink.right + size.w * 0.72;
  const x = clamp(wanted, leftLimit, rightLimit);
  const clear = x >= ink.right + size.w * 0.42;
  const y = clear ? ink.midY : Math.max(size.h * 0.7, ink.top - size.h * 0.8);
  return { x, y, rest };
}

function besideKicker(ctx, from) {
  const { size, kicker, h1, edges } = ctx;
  const line = kicker || h1;
  const y = (line?.midY ?? size.h) - 1;
  const anchorRight = line?.right ?? edges.left;
  const x = clamp(anchorRight + size.w * 0.58 + 12, edges.left + size.w * 0.5, edges.right - size.w * 0.46);
  const rest = from === 'right' ? Math.PI - 5 * DEG : 6 * DEG;
  return { x, y, rest };
}

function cock(from) {
  return from === 'right' ? Math.PI + 13 * DEG : -13 * DEG;
}

function fitSpot(spot, ctx, pose) {
  const parked = spot || besideKicker(ctx, 'left');
  if (pose === 'lip-left' || pose === 'lip-right') return clampSpot(parked, ctx);
  const { size } = ctx;
  const shifts = [
    [0, 0],
    [0, -8],
    [0, -18],
    [size.w * 0.28, -10],
    [-size.w * 0.22, -10],
    [0, -size.h * 0.9],
    [size.w * 0.5, 0],
    [0, size.h * 0.4],
  ];
  for (const [dx, dy] of shifts) {
    const next = clampSpot({ ...parked, x: parked.x + dx, y: parked.y + dy }, ctx);
    if (!hits(next.x, next.y, size, ctx.obstacles, 4)) return next;
  }
  return clampSpot(parked, ctx);
}

function clampSpot(spot, ctx) {
  const { size } = ctx;
  const left = size.w * 0.48 + 8;
  const right = Math.max(left, ctx.width - size.w * 0.48 - 8);
  const top = size.h * 0.48 + 4;
  let bottom = Math.max(top, Math.min(ctx.height - size.h * 0.15, ctx.fold - 8));
  if (ctx.aside && !ctx.wide) bottom = Math.max(top, Math.min(bottom, ctx.aside.bottom - size.h * 0.42));
  return {
    ...spot,
    x: clamp(spot.x, left, right),
    y: clamp(spot.y, top, bottom),
  };
}

function hits(x, y, size, obstacles, pad) {
  const rect = {
    left: x - size.w * 0.44 - pad,
    right: x + size.w * 0.44 + pad,
    top: y - size.h * 0.4 - pad,
    bottom: y + size.h * 0.4 + pad,
  };
  return obstacles.some((o) => rect.left < o.right && rect.right > o.left && rect.top < o.bottom && rect.bottom > o.top);
}

function cluster(el, sceneRect) {
  if (!el) return null;
  const kids = [...el.querySelectorAll('a, button')].filter((kid) => kid.getBoundingClientRect().width > 1);
  const nodes = kids.length ? kids : [el];
  let left = Infinity;
  let right = -Infinity;
  let top = Infinity;
  let bottom = -Infinity;
  nodes.forEach((node) => {
    const rect = node.getBoundingClientRect();
    left = Math.min(left, rect.left);
    right = Math.max(right, rect.right);
    top = Math.min(top, rect.top);
    bottom = Math.max(bottom, rect.bottom);
  });
  return {
    left: left - sceneRect.left,
    right: right - sceneRect.left,
    top: top - sceneRect.top,
    bottom: bottom - sceneRect.top,
    midY: (top + bottom) / 2 - sceneRect.top,
  };
}

function inkOf(el, sceneRect) {
  if (!el) return null;
  const rect = inkBox(el);
  if (rect.right - rect.left < 1) return null;
  return {
    left: rect.left - sceneRect.left,
    right: rect.right - sceneRect.left,
    top: rect.top - sceneRect.top,
    bottom: rect.bottom - sceneRect.top,
    midY: rect.midY - sceneRect.top,
  };
}

function obstaclesIn(scene, sceneRect) {
  const list = [];
  const inkTags = new Set(['H1', 'H2', 'H3', 'P', 'DT', 'DD', 'FIGCAPTION', 'LEGEND']);
  scene.querySelectorAll('h1, h2, h3, p, a, button, input, textarea, label, legend, dt, dd, figcaption').forEach((el) => {
    if (el.closest('[data-plane-photo], [data-paper-plane], .hp')) return;
    const raw = inkTags.has(el.tagName) ? inkBox(el) : el.getBoundingClientRect();
    const rect = raw.width ? raw : { left: raw.left, right: raw.right, top: raw.top, bottom: raw.bottom, width: raw.right - raw.left, height: raw.bottom - raw.top };
    if (rect.width < 2 || rect.height < 2) return;
    list.push({
      left: rect.left - sceneRect.left,
      right: rect.right - sceneRect.left,
      top: rect.top - sceneRect.top,
      bottom: rect.bottom - sceneRect.top,
    });
  });
  return list;
}

function routeFor(spot, pose, ctx) {
  const { size } = ctx;
  const from = spot.from;
  const dir = from === 'right' ? -1 : 1;
  const kickerTop = ctx.kicker?.top ?? ctx.h1?.top ?? size.h * 2;
  const ceiling = (ctx.logo?.bottom ?? 0) + size.h * 0.55;
  const above = kickerTop - size.h * 0.72;
  const canCruiseAbove = above > ceiling + 4 && above > size.h * 0.55;
  const land = {
    ...spot,
    width: ctx.width,
    cruiseY: canCruiseAbove ? above : spot.y,
    breath: canCruiseAbove ? 6 : 0,
    style: 'glide',
    trick: '',
    dropX: dir > 0 ? spot.x - size.w : spot.x + size.w,
    wiggle: 0,
    hop: 0,
    loopR: 0,
    loopCx: 0,
    loopCy: 0,
  };

  if (pose === 'lip-left' || pose === 'lip-right') {
    const photoTop = ctx.photo?.top ?? spot.y;
    land.cruiseY = clamp(canCruiseAbove ? above : photoTop - size.h * 0.85, size.h * 0.58, Math.max(size.h * 0.58, photoTop - 6));
    land.breath = 8;
    if (pose === 'lip-left') {
      const textRight = ctx.h1?.right ?? spot.x - size.w;
      land.dropX = Math.min(spot.x - size.w * 0.4, Math.max(textRight + size.w * 0.2, spot.x - size.w * 2.6));
    } else {
      const photoLeft = ctx.photo?.left ?? spot.x;
      land.dropX = Math.max(spot.x + size.w * 0.5, photoLeft + size.w * 0.3);
    }
    return land;
  }

  if (pose === 'present') {
    // Level with the buttons, a low hill over the photo, then into the gutter.
    land.cruiseY = spot.y;
    land.breath = 14;
    land.dropX = spot.x + size.w * 1.6;
    land.startX = ctx.width + size.w * 0.45;
    return land;
  }

  if (pose === 'sky') {
    land.cruiseY = spot.y;
    land.breath = 12;
    land.dropX = spot.x - size.w * 2.2;
    land.startX = -size.w * 0.55;
    return land;
  }

  if (pose === 'deliver' || pose === 'cheers' || pose === 'jaunt') return loopPlan(land, ctx, pose);

  land.dropX = dir > 0 ? spot.x - size.w * 0.8 : spot.x + size.w * 0.8;
  return land;
}

function loopPlan(land, ctx, pose) {
  const { size } = ctx;
  const from = land.from;
  const contentTop = Math.min(ctx.kicker?.top ?? Infinity, ctx.h1?.top ?? Infinity, ctx.form?.top ?? Infinity);
  const half = size.h * 0.52;
  const bandTop = Math.max(half + 6, (ctx.logo?.bottom ?? 0) + half * 0.35);
  const bandBottom = (Number.isFinite(contentTop) ? contentTop : land.y) - half - 16;
  const room = bandBottom - bandTop;
  if (room < 36) return land;
  const cap = pose === 'cheers' ? 72 : 56;
  let loopR = clamp(Math.min(room * 0.48, size.w * 0.62), 18, cap);
  if (loopR * 2 > room - 4) loopR = Math.max(16, (room - 4) / 2);
  const loopBottom = Math.min(bandBottom, bandTop + loopR * 2 + 4);
  const loopCy = loopBottom - loopR;
  const inset = loopR + size.w * 0.28;
  const reach = Math.max(size.w * 1.7, loopR * 2.1);
  let cx = from === 'right' ? land.x + reach : land.x - reach;
  cx = clamp(cx, inset, Math.max(inset, ctx.width - inset));
  if (from === 'right' && cx < land.x + loopR * 0.8) cx = clamp(land.x + loopR * 1.15, land.x + 16, ctx.width - inset);
  if (from !== 'right' && cx > land.x - loopR * 0.8) cx = clamp(land.x - loopR * 1.15, inset, land.x - 16);

  return {
    ...land,
    trick: 'loop',
    style: 'loop',
    loopR,
    loopCx: cx,
    loopCy,
    wiggle: Math.min(8, size.h * 0.18),
    hop: Math.min(8, size.h * 0.18),
  };
}

function buildVisitPath(land, size) {
  const points = land.trick === 'loop'
    ? loopRoute(land, size)
    : land.style === 'arch'
      ? archRoute(land, size)
      : glideRoute(land, size);
  return pace(alignEnding(measure(points), land.rest));
}

function glideRoute(land, size) {
  const dir = land.from === 'right' ? -1 : 1;
  const startX = Number.isFinite(land.startX) ? land.startX : (dir > 0 ? -size.w * 0.55 : land.width + size.w * 0.55);
  const endX = land.x;
  const cruiseY = land.cruiseY;
  const minSpan = 24;
  let dropX = land.dropX;
  if (dir > 0) {
    const lo = startX + minSpan;
    const hi = endX - 8;
    dropX = hi > lo ? clamp(dropX, lo, hi) : (startX + endX) / 2;
  } else {
    const lo = endX + 8;
    const hi = startX - minSpan;
    dropX = hi > lo ? clamp(dropX, lo, hi) : (startX + endX) / 2;
  }
  const points = [];
  const cruiseSteps = Math.max(6, Math.round(Math.abs(dropX - startX) / 18));
  for (let i = 0; i <= cruiseSteps; i += 1) {
    const u = i / cruiseSteps;
    points.push({ x: startX + (dropX - startX) * u, y: cruiseY - Math.sin(u * Math.PI) * (land.breath || 0) });
  }
  const dropXSpan = endX - dropX;
  const dropYSpan = land.y - cruiseY;
  const dropSteps = Math.max(8, Math.round(Math.hypot(dropXSpan, dropYSpan) / 14));
  for (let i = 1; i <= dropSteps; i += 1) {
    const u = i / dropSteps;
    const e = u * u * (3 - 2 * u);
    points.push({
      x: dropX + dropXSpan * u,
      y: cruiseY + dropYSpan * e - Math.sin((1 - u) * Math.PI) * (land.breath || 0) * (1 - u),
    });
  }
  return points;
}

function archRoute(land, size) {
  const startX = Number.isFinite(land.startX) ? land.startX : land.width + size.w * 0.4;
  const rise = Math.min(land.breath || 0, Math.max(0, land.cruiseY - size.h * 0.5));
  const points = [];
  const steps = 44;
  for (let i = 0; i <= steps; i += 1) {
    const u = i / steps;
    const e = u * u * (3 - 2 * u);
    points.push({
      x: startX + (land.x - startX) * u,
      y: land.cruiseY + (land.y - land.cruiseY) * e - Math.sin(u * Math.PI) * rise,
    });
  }
  return points;
}

function loopRoute(land, size) {
  const dir = land.from === 'right' ? -1 : 1;
  const r = land.loopR;
  const cx = land.loopCx;
  const cy = land.loopCy;
  const entryX = cx;
  const entryY = cy + r;
  const startX = dir > 0 ? -size.w * 0.7 : land.width + size.w * 0.7;
  const points = [];
  const leadSteps = Math.max(10, Math.round(Math.abs(entryX - startX) / 16));
  for (let i = 0; i <= leadSteps; i += 1) {
    const u = i / leadSteps;
    const e = u * u * (3 - 2 * u);
    points.push({ x: startX + (entryX - startX) * e, y: entryY });
  }
  const steps = 60;
  for (let i = 1; i <= steps; i += 1) {
    const u = i / steps;
    const theta = dir > 0 ? -Math.PI / 2 + u * Math.PI * 2 : -Math.PI / 2 - u * Math.PI * 2;
    points.push({ x: cx + r * Math.cos(theta), y: cy - r * Math.sin(theta) });
  }
  const exit = points[points.length - 1];
  const span = Math.abs(land.x - exit.x) || 1;
  const exitSteps = 32;
  for (let i = 1; i <= exitSteps; i += 1) {
    const u = i / exitSteps;
    const e = u * u * (3 - 2 * u);
    const x = exit.x + (land.x - exit.x) * e;
    const remain = Math.abs(land.x - x) / span;
    const drop = remain > 0.34 ? 0 : (0.34 - remain) / 0.34;
    const flare = drop * drop * (3 - 2 * drop);
    const hop = Math.sin(Math.min(1, drop) * Math.PI) * (land.hop || 0);
    const wiggle = Math.sin(u * Math.PI * 2) * (land.wiggle || 0) * (1 - u);
    points.push({ x, y: exit.y + (land.y - exit.y) * flare - hop + wiggle });
  }
  return points;
}

function placeAt(point, rest) {
  return { x: point.x, y: point.y, rest };
}

function greetingPath(land, size) {
  const endX = land.x;
  const startX = Math.min(land.startX, endX - size.w);
  const sky = Math.min(land.sky, land.y - size.h * 0.4);
  // Paper only fills the middle of the viewBox, so this keeps the wing above the name.
  const clearY = Math.min(land.y - size.h * 0.15, land.nameTop - size.h * 0.32 - 2);
  const sinkX = Math.min(endX - 4, land.nameRight + size.w * 0.5);
  const dropX = Math.max(startX + 24, Math.min(land.kickerRight + 12, sinkX - size.w * 0.8));
  const points = [];

  const levelSteps = 12;
  const levelSpan = Math.max(0, dropX - startX);
  for (let i = 0; i <= levelSteps; i += 1) {
    points.push({ x: startX + levelSpan * (i / levelSteps), y: sky });
  }

  const glideSteps = 28;
  const glideSpan = Math.max(1, sinkX - dropX);
  for (let i = 1; i <= glideSteps; i += 1) {
    const u = i / glideSteps;
    points.push({
      x: dropX + glideSpan * u,
      y: sky + (clearY - sky) * u,
    });
  }

  const sinkSteps = 16;
  const sinkSpan = Math.max(1, endX - sinkX);
  for (let i = 1; i <= sinkSteps; i += 1) {
    const u = i / sinkSteps;
    const eased = 1 - (1 - u) ** 2;
    points.push({
      x: sinkX + sinkSpan * u,
      y: clearY + (land.y - clearY) * eased,
    });
  }

  return alignEnding(measure(points), land.rest);
}

function inkBox(el) {
  const range = document.createRange();
  range.selectNodeContents(el);
  const rect = range.getBoundingClientRect();
  if (rect.width > 0.5 && rect.height > 0.5) {
    return {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      midY: (rect.top + rect.bottom) / 2,
    };
  }
  const fallback = el.getBoundingClientRect();
  return {
    left: fallback.left,
    right: fallback.right,
    top: fallback.top,
    bottom: fallback.bottom,
    midY: (fallback.top + fallback.bottom) / 2,
  };
}

function greetEase(t) {
  return 1 - (1 - t) ** 1.7;
}

function armPlaneClick({ plane, craft, reduce, getSize, getPlace, apply, setPlaying, onLift, onRest }) {
  let raf = 0;
  const tumble = plane.dataset.play === 'tumble';
  craft.addEventListener('click', (event) => {
    if (plane.dataset.state !== 'landed') return;
    event.preventDefault();
    event.stopPropagation();
    cancelAnimationFrame(raf);
    onLift?.();
    setPlaying(true);
    plane.dataset.state = 'startled';
    plane.classList.remove('is-resting');
    void craft.offsetWidth;
    const size = getSize();
    const perch = getPlace();
    const sign = Math.cos(perch.rest) < 0 ? 1 : -1;
    const radius = clamp(size.w * (tumble ? 0.58 : 0.5), 30, tumble ? 58 : 52);
    const room = skyRoom(craft);
    const nose = size.w * 0.42;
    const loopFits = room >= radius * 2 + size.h * 0.4 && room >= radius + nose + 8;
    const hop = Math.min(size.h * 1.05, Math.max(12, (room - nose - 10) / 0.75));
    const gentle = reduce.matches;
    const duration = gentle ? 480 : tumble ? 1760 : 1360;
    let origin = 0;

    const finish = () => {
      const place = getPlace();
      apply(place, { heading: place.rest });
      setPlaying(false);
      plane.dataset.state = 'landed';
      requestAnimationFrame(() => plane.classList.add('is-resting'));
      onRest?.();
    };

    const step = (now) => {
      if (!origin) origin = now;
      const t = clamp((now - origin) / duration, 0, 1);
      const pose = gentle
        ? nodPose(t, perch)
        : loopFits
          ? (tumble ? tumblePose(t, perch, radius, sign) : loopPose(t, perch, radius, sign))
          : flickPose(t, perch, sign, hop);
      apply(
        { x: pose.x, y: pose.y, rest: perch.rest },
        {
          heading: pose.heading,
          bank: pose.bank,
          altitude: pose.altitude,
          wobble: pose.wobble,
          shadowX: perch.x,
          shadowY: perch.y,
        },
      );
      if (t >= 1) {
        finish();
        return;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  });
}

function skyRoom(craft) {
  const box = craft.getBoundingClientRect();
  const centerY = box.top + box.height / 2;
  let ceiling = 8;
  let node = craft.parentElement;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (style.overflow !== 'visible' || style.overflowY !== 'visible') {
      ceiling = Math.max(ceiling, node.getBoundingClientRect().top + 6);
    }
    node = node.parentElement;
  }
  const bar = document.querySelector('header.sticky');
  if (bar) {
    const band = bar.getBoundingClientRect();
    if (band.height > 0 && band.bottom > 0 && band.top < centerY) {
      ceiling = Math.max(ceiling, band.bottom + 8);
    }
  }
  return centerY - ceiling;
}

function flickPose(t, perch, sign, hop) {
  if (t < 0.12) {
    const e = Math.sin((t / 0.12) * Math.PI);
    return {
      x: perch.x - Math.cos(perch.rest) * 5 * e,
      y: perch.y + 2 * e,
      heading: perch.rest - sign * 7 * DEG * e,
      bank: 1,
      altitude: 0,
      wobble: 0,
    };
  }
  if (t < 0.4) {
    const u = (t - 0.12) / 0.28;
    const e = 1 - (1 - u) ** 2;
    const lift = Math.sin(e * Math.PI / 2) * hop;
    return {
      x: perch.x,
      y: perch.y - lift,
      heading: perch.rest + sign * Math.PI * e,
      bank: 1 - 0.05 * e,
      altitude: lift,
      wobble: Math.sin(e * Math.PI) * 1.1,
    };
  }
  if (t < 0.58) {
    const u = (t - 0.4) / 0.18;
    const float = Math.sin(u * Math.PI) * 3;
    return {
      x: perch.x,
      y: perch.y - hop - float,
      heading: perch.rest + sign * Math.PI,
      bank: 0.96,
      altitude: hop + float,
      wobble: Math.sin(u * Math.PI * 2) * 0.8,
    };
  }
  if (t < 0.86) {
    const u = (t - 0.58) / 0.28;
    const e = u ** 2;
    const lift = Math.cos(e * Math.PI / 2) * hop;
    return {
      x: perch.x,
      y: perch.y - lift,
      heading: perch.rest + sign * Math.PI * (1 + e),
      bank: 0.96 + 0.04 * e,
      altitude: lift,
      wobble: 0,
    };
  }
  const u = (t - 0.86) / 0.14;
  const damp = Math.sin(u * Math.PI) * (1 - u);
  return {
    x: perch.x,
    y: perch.y - 4 * damp,
    heading: perch.rest + sign * 4 * DEG * damp,
    bank: 1,
    altitude: 4 * damp,
    wobble: 0,
  };
}

function loopPose(t, perch, radius, sign) {
  if (t < 0.16) {
    const e = Math.sin((t / 0.16) * Math.PI);
    return {
      x: perch.x - Math.cos(perch.rest) * 6 * e,
      y: perch.y - Math.sin(perch.rest) * 6 * e + 2 * e,
      heading: perch.rest - sign * 8 * DEG * e,
      bank: 1 - 0.04 * e,
      altitude: 0,
      wobble: 0,
    };
  }
  if (t < 0.84) {
    const u = (t - 0.16) / 0.68;
    const e = u < 0.5 ? 2 * u * u : 1 - ((-2 * u + 2) ** 2) / 2;
    const heading = perch.rest + sign * Math.PI * 2 * e;
    const point = loopPoint(perch, radius, sign, heading);
    return {
      x: point.x,
      y: point.y,
      heading,
      bank: 1 - 0.08 * Math.sin(e * Math.PI),
      altitude: Math.max(0, perch.y - point.y),
      wobble: Math.sin(e * Math.PI * 2) * 1.2,
    };
  }
  const u = (t - 0.84) / 0.16;
  const damp = Math.sin(u * Math.PI) * (1 - u);
  return {
    x: perch.x,
    y: perch.y - 5 * damp,
    heading: perch.rest + sign * 5 * DEG * damp,
    bank: 1,
    altitude: 5 * damp,
    wobble: 0,
  };
}

function tumblePose(t, perch, radius, sign) {
  if (t < 0.08) {
    const e = Math.sin((t / 0.08) * Math.PI);
    return {
      x: perch.x - Math.cos(perch.rest) * 8 * e,
      y: perch.y + 2 * e,
      heading: perch.rest - sign * 12 * DEG * e,
      bank: 1 - 0.08 * e,
      altitude: 0,
      wobble: 0,
    };
  }
  if (t < 0.86) {
    const u = (t - 0.08) / 0.78;
    const e = u < 0.5 ? 2 * u * u : 1 - ((-2 * u + 2) ** 2) / 2;
    const heading = perch.rest + sign * Math.PI * 4 * e;
    const point = loopPoint(perch, radius, sign, heading);
    return {
      x: point.x,
      y: point.y,
      heading,
      bank: 1 - 0.18 * Math.abs(Math.sin(e * Math.PI * 2)),
      altitude: Math.max(0, perch.y - point.y),
      wobble: Math.sin(e * Math.PI * 6) * 2.4,
    };
  }
  const u = (t - 0.86) / 0.14;
  const damp = Math.sin(u * Math.PI) * (1 - u);
  return {
    x: perch.x,
    y: perch.y - 7 * damp,
    heading: perch.rest + sign * 8 * DEG * damp,
    bank: 1,
    altitude: 7 * damp,
    wobble: 0,
  };
}

function loopPoint(perch, radius, sign, heading) {
  const start = loopOffset(sign, perch.rest);
  const now = loopOffset(sign, heading);
  return {
    x: perch.x + radius * (now.x - start.x),
    y: perch.y + radius * (now.y - start.y),
  };
}

function loopOffset(sign, heading) {
  if (sign > 0) return { x: Math.sin(heading), y: -Math.cos(heading) };
  return { x: -Math.sin(heading), y: Math.cos(heading) };
}

function nodPose(t, perch) {
  const hop = Math.sin(t * Math.PI) * 6;
  const wag = Math.sin(t * Math.PI * 2) * 4 * DEG * (1 - t);
  return {
    x: perch.x,
    y: perch.y - hop,
    heading: perch.rest + wag,
    bank: 1,
    altitude: hop,
    wobble: 0,
  };
}

function buildPath(land, size) {
  return pace(alignEnding(measure(homeRoute(land, size)), land.rest));
}

// A straight entrance, one loop a size larger than the contact-page loop, then the landing bank.
function homeRoute(land, size) {
  const entryH = 3 * DEG;
  const minY = size.h * 0.72;
  const maxX = land.contentRight - size.h * 0.62 - 8;
  const targetY = clamp(
    (land.h1Top ?? minY + size.h) - size.h * 0.62,
    minY + 4,
    (land.h1Top ?? land.y) - size.h * 0.28,
  );
  const startX = -size.w * 0.45;
  let best = null;

  for (let ease = 0.08; ease <= 0.16; ease += 0.04) {
    for (let glide = size.w * 0.4; glide <= size.w * 1.2; glide += size.w * 0.2) {
      const hi = Math.max(size.h, land.y - minY);
      for (let radius = size.h * 1.05; radius <= hi; radius += Math.max(8, size.h * 0.22)) {
        const shaped = shapeTurn(land, radius, ease, glide, entryH);
        if (shaped.minY < minY || shaped.maxX > maxX || shaped.start.x < startX + size.w * 0.8) continue;
        const score = Math.abs(shaped.start.y - targetY) + ease * 36 - Math.min(shaped.radius, size.w * 2.2) * 0.04;
        if (!best || score < best.score) best = { ...shaped, score };
      }
    }
  }

  const shaped = best ?? shapeTurn(land, size.w, 0.08, size.w * 0.6, entryH);
  return withHomeLoop(leadIn(shaped.points, startX, entryH), land, size);
}

// Contact loops cap at 56. This one aims a size larger and shrinks only to stay off the type.
function homeLoopWant(size) {
  return clamp(size.w * 1.02, 72, 108);
}

function withHomeLoop(points, land, size) {
  const want = homeLoopWant(size);
  const tangent = tangentLoop(points, land, size, want);
  if (tangent && tangent.r >= want * 0.8) return spliceCircle(points, tangent);
  const pocket = pocketLoop(points, land, size, want);
  if (pocket && (!tangent || pocket.r > tangent.r + 4)) {
    const woven = weavePocket(points, pocket, land, size);
    if (woven) return woven;
  }
  return tangent ? spliceCircle(points, tangent) : points;
}

function tangentLoop(points, land, size, want) {
  const floor = Math.max(40, size.w * 0.56);
  let best = null;
  const last = Math.floor(points.length * 0.78);
  for (let index = 8; index < last; index += 2) {
    const point = points[index];
    if (Math.hypot(point.x - land.x, point.y - land.y) < size.w * 1.5) continue;
    const heading = headingOf(points, index);
    for (const sign of [-1, 1]) {
      const nx = -Math.sin(heading) * sign;
      const ny = Math.cos(heading) * sign;
      for (let r = want; r >= floor; r -= 4) {
        const cx = point.x + nx * r;
        const cy = point.y + ny * r;
        if (!circleClear(cx, cy, r, land, size)) continue;
        const early = 1 - index / points.length;
        if (!best || r > best.r + 0.5 || (r >= best.r - 0.5 && early > best.early)) {
          best = { index, cx, cy, r, early };
        }
        break;
      }
    }
  }
  return best;
}

function pocketLoop(points, land, size, want) {
  const floor = Math.max(44, size.w * 0.62);
  const reachX = size.w * 0.4;
  const reachY = size.h * 0.65;
  let best = null;
  const top = reachY;
  const bottom = Math.min(land.height - reachY, land.y + size.w);
  for (let r = want; r >= floor; r -= 4) {
    for (let cx = reachX + r; cx <= land.width - reachX - r; cx += 14) {
      for (let cy = top + r; cy <= bottom - r; cy += 14) {
        if (!circleClear(cx, cy, r, land, size)) continue;
        if (Math.hypot(cx - land.x, cy - land.y) < r + size.w * 0.4) continue;
        let near = Infinity;
        for (let i = 8; i < points.length * 0.86; i += 3) {
          near = Math.min(near, Math.abs(Math.hypot(points[i].x - cx, points[i].y - cy) - r));
        }
        if (near > 48) continue;
        const score = r * 8 - near * 3;
        if (!best || score > best.score) best = { cx, cy, r, score };
      }
    }
    if (best) return best;
  }
  return null;
}

function weavePocket(points, circle, land, size) {
  const { cx, cy, r } = circle;
  let best = null;
  const last = Math.floor(points.length * 0.86);
  for (let index = 6; index < last; index += 2) {
    const point = points[index];
    const heading = headingOf(points, index);
    const theta = Math.atan2(point.y - cy, point.x - cx);
    const entry = { x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) };
    const gap = Math.hypot(entry.x - point.x, entry.y - point.y);
    if (gap > 42) continue;
    const tangent = Math.atan2(Math.cos(theta), -Math.sin(theta));
    const align = Math.min(
      Math.abs(shortest(heading, tangent)),
      Math.abs(shortest(heading, tangent + Math.PI)),
    );
    if (align > 0.65) continue;
    const score = gap + align * 36;
    if (!best || score < best.score) best = { index, score };
  }
  if (!best) return null;
  const index = best.index;
  const point = points[index];
  const heading = headingOf(points, index);
  const theta = Math.atan2(point.y - cy, point.x - cx);
  const entry = { x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) };
  const tangent = Math.atan2(Math.cos(theta), -Math.sin(theta));
  const dir = Math.cos(shortest(tangent, heading)) >= 0 ? 1 : -1;
  const entryH = dir > 0 ? tangent : tangent + Math.PI;
  const approach = bend(point, entry, heading, entryH);
  if (approach.some((spot) => Math.hypot(spot.x - land.x, spot.y - land.y) > size.w && !pointRoom(spot, land, size))) return null;
  const steps = Math.max(64, Math.round((Math.PI * 2 * r) / 8));
  const arc = [];
  for (let i = 1; i < steps; i += 1) {
    const angle = theta + dir * (i / steps) * Math.PI * 2;
    arc.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
  }
  const back = bend(entry, point, entryH, heading);
  return points.slice(0, index).concat(approach, arc, back, points.slice(index));
}

function spliceCircle(points, loop) {
  const { index, cx, cy, r } = loop;
  const point = points[index];
  const theta0 = Math.atan2(point.y - cy, point.x - cx);
  const tangent = Math.atan2(Math.cos(theta0), -Math.sin(theta0));
  const heading = headingOf(points, index);
  const dir = Math.cos(shortest(tangent, heading)) >= 0 ? 1 : -1;
  const steps = Math.max(64, Math.round((Math.PI * 2 * r) / 8));
  const arc = [];
  for (let i = 1; i < steps; i += 1) {
    const theta = theta0 + dir * (i / steps) * Math.PI * 2;
    arc.push({ x: cx + r * Math.cos(theta), y: cy + r * Math.sin(theta) });
  }
  return points.slice(0, index).concat(arc, points.slice(index));
}

function bend(from, to, fromH, toH) {
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  const handle = clamp(dist * 0.48, 12, 96);
  const c1 = { x: from.x + Math.cos(fromH) * handle, y: from.y + Math.sin(fromH) * handle };
  const c2 = { x: to.x - Math.cos(toH) * handle, y: to.y - Math.sin(toH) * handle };
  const count = Math.max(5, Math.round(dist / 11));
  const points = [];
  for (let i = 1; i < count; i += 1) {
    const t = i / count;
    const u = 1 - t;
    points.push({
      x: u ** 3 * from.x + 3 * u ** 2 * t * c1.x + 3 * u * t ** 2 * c2.x + t ** 3 * to.x,
      y: u ** 3 * from.y + 3 * u ** 2 * t * c1.y + 3 * u * t ** 2 * c2.y + t ** 3 * to.y,
    });
  }
  return points;
}

function circleClear(cx, cy, r, land, size) {
  const reach = size.w * 0.42;
  const bounds = {
    left: size.w * 0.4,
    right: land.width - size.w * 0.36,
    top: size.h * 0.7,
    bottom: land.height - size.h * 0.4,
  };
  const steps = 56;
  for (let i = 0; i < steps; i += 1) {
    const theta = (i / steps) * Math.PI * 2;
    const x = cx + r * Math.cos(theta);
    const y = cy + r * Math.sin(theta);
    if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) return false;
    if (hitsInk(x, y, land.obstacles, reach)) return false;
  }
  return true;
}

function pointRoom(point, land, size) {
  if (point.x < size.w * 0.28 || point.x > land.width - size.w * 0.24) return false;
  if (point.y < size.h * 0.4 || point.y > land.height - size.h * 0.25) return false;
  if (Math.hypot(point.x - land.x, point.y - land.y) < size.w * 0.85) return true;
  return !hitsInk(point.x, point.y, land.obstacles, size.h * 0.5);
}

function hitsInk(x, y, obstacles, reach) {
  return (obstacles || []).some((box) => {
    const qx = clamp(x, box.left, box.right);
    const qy = clamp(y, box.top, box.bottom);
    return Math.hypot(x - qx, y - qy) < reach;
  });
}

function headingOf(points, index) {
  const a = points[Math.max(0, index - 2)];
  const b = points[Math.min(points.length - 1, index + 2)];
  return Math.atan2(b.y - a.y, b.x - a.x);
}

function shapeTurn(land, radius, ease, glide, entryH) {
  const theta = land.rest - entryH;
  const ramp = Math.max(theta * ease, 1e-3);
  const points = [];
  let x = 0;
  let y = 0;
  let heading = entryH;
  let turned = 0;
  while (turned < theta - 1e-5) {
    const remain = theta - turned;
    let curve = 1 / radius;
    if (turned < ramp) curve = Math.max(turned / ramp, 0.04) / radius;
    else if (remain < ramp) curve = Math.max(remain / ramp, 0.04) / radius;
    const dh = Math.min(1.1 * DEG, remain);
    const mid = heading + dh / 2;
    const ds = dh / curve;
    x += Math.cos(mid) * ds;
    y += Math.sin(mid) * ds;
    heading += dh;
    turned += dh;
    points.push({ x, y });
  }
  const steps = Math.max(5, Math.round(glide / 12));
  for (let step = 1; step <= steps; step += 1) {
    x += Math.cos(land.rest) * (glide / steps);
    y += Math.sin(land.rest) * (glide / steps);
    points.push({ x, y });
  }
  const last = points[points.length - 1];
  const dx = land.x - last.x;
  const dy = land.y - last.y;
  let minY = Infinity;
  let maxX = -Infinity;
  points.forEach((point) => {
    point.x += dx;
    point.y += dy;
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
  });
  return { points, minY, maxX, start: points[0], radius };
}

function leadIn(points, startX, entryH) {
  const first = points[0];
  const span = first.x - startX;
  if (span < 16) return points;
  const lead = [];
  const steps = Math.max(10, Math.round(span / 14));
  for (let step = 0; step <= steps; step += 1) {
    const x = startX + span * (step / steps);
    lead.push({ x, y: first.y - (first.x - x) * Math.tan(entryH) });
  }
  lead.pop();
  return lead.concat(points);
}

function pace(path) {
  const curvature = path.pts.map((point, index) => {
    if (!index || index === path.pts.length - 1) return 0;
    const prev = path.pts[index - 1];
    const next = path.pts[index + 1];
    const incoming = Math.atan2(point.y - prev.y, point.x - prev.x);
    const outgoing = Math.atan2(next.y - point.y, next.x - point.x);
    const span = Math.max(1, (next.d - prev.d) / 2);
    return Math.abs(shortest(incoming, outgoing)) / span;
  });
  for (let index = curvature.length - 2; index >= 0; index -= 1) {
    curvature[index] = Math.max(curvature[index], curvature[index + 1] * 0.96);
  }
  for (let index = 1; index < curvature.length; index += 1) {
    curvature[index] = Math.max(curvature[index], curvature[index - 1] * 0.9);
  }
  let clock = 0;
  path.pts.forEach((point, index) => {
    if (index) {
      const prev = path.pts[index - 1];
      const span = point.d - prev.d;
      const dive = Math.abs(Math.sin(Math.atan2(point.y - prev.y, point.x - prev.x)));
      const speed = clamp(1 / (1 + curvature[index] * 48), 0.64, dive > 0.9 ? 1.12 : 1);
      clock += span / speed;
    }
    point.clock = clock;
  });
  path.clock = clock || 1;
  return path;
}

function alignEnding(path, rest) {
  const begin = path.length - Math.min(72, path.length * 0.045);
  const dirX = Math.cos(rest);
  const dirY = Math.sin(rest);
  const end = path.pts[path.pts.length - 1];
  path.pts.forEach((point) => {
    if (point.d <= begin) return;
    const u = (point.d - begin) / (path.length - begin);
    const blend = u * u * (3 - 2 * u);
    const remain = path.length - point.d;
    point.x += (end.x - dirX * remain - point.x) * blend;
    point.y += (end.y - dirY * remain - point.y) * blend;
  });
  return measure(path.pts);
}

function measure(points) {
  const pts = [];
  let length = 0;
  points.forEach((point, index) => {
    if (index) length += Math.hypot(point.x - points[index - 1].x, point.y - points[index - 1].y);
    pts.push({ x: point.x, y: point.y, d: length });
  });
  return { pts, length };
}

function pointAt(path, distance) {
  const target = clamp(distance, 0, path.length);
  const pts = path.pts;
  let lo = 0;
  let hi = pts.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (pts[mid].d < target) lo = mid;
    else hi = mid;
  }
  const a = pts[lo];
  const b = pts[hi];
  const span = b.d - a.d || 1;
  const u = (target - a.d) / span;
  return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
}

function pointAtClock(path, clock) {
  const target = clamp(clock, 0, path.clock);
  const pts = path.pts;
  let lo = 0;
  let hi = pts.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (pts[mid].clock < target) lo = mid;
    else hi = mid;
  }
  const a = pts[lo];
  const b = pts[hi];
  const span = b.clock - a.clock || 1;
  const u = (target - a.clock) / span;
  return {
    x: a.x + (b.x - a.x) * u,
    y: a.y + (b.y - a.y) * u,
    d: a.d + (b.d - a.d) * u,
  };
}

function tangentAt(path, distance) {
  const delta = Math.max(8, path.length * 0.01);
  const a = pointAt(path, distance - delta);
  const b = pointAt(path, distance + delta);
  return Math.atan2(b.y - a.y, b.x - a.x);
}

function ease(t) {
  // Steady through both crossings, then a soft arrival.
  if (t < 0.84) return (t / 0.84) * 0.9;
  const u = (t - 0.84) / 0.16;
  return 0.9 + (1 - (1 - u) ** 2) * 0.1;
}

function shortest(from, to) {
  const tau = Math.PI * 2;
  return ((to - from + Math.PI) % tau + tau) % tau - Math.PI;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
